import { NodeChange } from '@xyflow/react';
import { useEffect, useMemo, useRef } from 'react';
import { Control, FieldValues, Path, useWatch } from 'react-hook-form';

import { Node } from '@/components/diagrams/FlowChart/types';
import { isTileNode } from '@/components/diagrams/FlowChart/utils/nodes';

export interface TileSelectionSyncInput<T extends FieldValues> {
  control: Control<T>;
  fieldName: Path<T>;
  setValue: (name: Path<T>, value: string[]) => void;
  nodes: Node[];
  dispatch: (action: {
    type: 'NODES_CHANGED';
    nodes: Node[];
    changes: NodeChange[];
    isInteractive?: boolean;
  }) => void;
}

export function getSelectedTileIdsFromNodes(nodes: Node[]): string[] {
  return nodes
    .filter((node) => node.selected && isTileNode(node))
    .map((node) => node.id);
}

export function getUpdatedNodesFromSelection(
  nodes: Node[],
  selectedTileIds: string[]
): Node[] {
  const selectedSet = new Set(selectedTileIds);
  return nodes.map((node) => {
    if (!isTileNode(node)) return node;
    const shouldBeSelected = selectedSet.has(node.id);
    if (node.selected === shouldBeSelected) return node;
    return { ...node, selected: shouldBeSelected };
  });
}

export function useTileSelectionSync<T extends FieldValues>({
  control,
  fieldName,
  setValue,
  nodes,
  dispatch,
}: TileSelectionSyncInput<T>) {
  const isSyncingFromContextRef = useRef(false);
  const isSyncingFromFormRef = useRef(false);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const selectedTileIdsFromContext = useMemo(
    () => getSelectedTileIdsFromNodes(nodes),
    [nodes]
  );

  const selectedTileIds =
    useWatch({
      control,
      name: fieldName,
    }) ?? ([] as string[]);

  // Sync context → form (only when context changes, not form)
  useEffect(() => {
    if (isSyncingFromFormRef.current) {
      isSyncingFromFormRef.current = false;
      return;
    }

    const contextSet = new Set(selectedTileIdsFromContext);
    const formSet = new Set(selectedTileIds);
    const isSynced =
      contextSet.size === formSet.size &&
      selectedTileIdsFromContext.every((id) => formSet.has(id));

    if (!isSynced) {
      isSyncingFromContextRef.current = true;
      setValue(fieldName, selectedTileIdsFromContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTileIdsFromContext]);

  // Sync form → context (only when form changes, not context)
  useEffect(() => {
    if (isSyncingFromContextRef.current) {
      isSyncingFromContextRef.current = false;
      return;
    }

    const contextSet = new Set(selectedTileIdsFromContext);
    const formSet = new Set(selectedTileIds);
    const isSynced =
      contextSet.size === formSet.size &&
      selectedTileIds.every((id) => contextSet.has(id));

    if (!isSynced) {
      isSyncingFromFormRef.current = true;
      const updatedNodes = getUpdatedNodesFromSelection(
        nodesRef.current,
        selectedTileIds
      );
      dispatch({ type: 'NODES_CHANGED', nodes: updatedNodes, changes: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTileIds]);

  return { selectedTileIds };
}
